<script setup>
/**
 * AppIcon — the single entry point for UI-chrome iconography.
 *
 * House rules enforced here so individual call sites cannot drift:
 *   - Lucide icons only, resolved by kebab-case `name` from a curated registry
 *     (explicit imports keep tree-shaking intact — do NOT switch to `import *`).
 *   - Sizes: 16 (inline with text), 20 (nav/buttons), 24 (feature cards / empty states).
 *   - stroke-width 1.75, color via currentColor (dark mode works for free).
 *   - Decorative by default (aria-hidden); pass `label` for meaningful icons
 *     to get role="img" + aria-label instead.
 *
 * Adding an icon: import it, add a kebab-case entry to `registry`.
 */
import { computed } from 'vue'
import {
  Activity, ArrowLeft, ArrowRight, ArrowUp, BadgeCheck, Banknote, Battery,
  BookOpen, Briefcase, Building, Calculator, Calendar, CalendarClock, Camera,
  Car, ChartColumn, Check, CheckCheck, ChevronDown, ChevronRight, CircleCheck, CircleDot,
  CircleHelp, CircleX, ClipboardList, Clock, CloudSun, CreditCard, Download,
  Eye, FileSignature, FileText, Flame, FolderOpen, Handshake, HardHat, Heart,
  Home, Image, Info, KeyRound, Landmark, LayoutGrid, LifeBuoy, Lightbulb,
  Link, LoaderCircle, Lock, LockOpen, LogOut, MapPin, Menu, MessageCircle,
  Moon, Package, Paperclip, PartyPopper, Pencil, PencilRuler, Play, Plug,
  Plus, Receipt, RefreshCw, Rocket, SatelliteDish, Save, Scale, ScrollText,
  Search, Send, Settings, Shield, ShieldAlert, ShieldCheck, Signature, Siren,
  Sparkles, Square, Star, Stethoscope, Store, Sun, Target, Trees,
  TrendingDown, TrendingUp, TriangleAlert, Trophy, Upload, User, UserCog,
  Users, Wallet, Waves, Wrench, X, Zap,
} from 'lucide-vue-next'

const registry = {
  'activity': Activity,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'badge-check': BadgeCheck,
  'banknote': Banknote,
  'battery': Battery,
  'book-open': BookOpen,
  'briefcase': Briefcase,
  'building': Building,
  'calculator': Calculator,
  'calendar': Calendar,
  'calendar-clock': CalendarClock,
  'camera': Camera,
  'car': Car,
  'chart-column': ChartColumn,
  'check': Check,
  'check-check': CheckCheck,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'circle-check': CircleCheck,
  'circle-dot': CircleDot,
  'circle-help': CircleHelp,
  'circle-x': CircleX,
  'clipboard-list': ClipboardList,
  'clock': Clock,
  'cloud-sun': CloudSun,
  'credit-card': CreditCard,
  'download': Download,
  'eye': Eye,
  'file-signature': FileSignature,
  'file-text': FileText,
  'flame': Flame,
  'folder-open': FolderOpen,
  'handshake': Handshake,
  'hard-hat': HardHat,
  'heart': Heart,
  'home': Home,
  'image': Image,
  'info': Info,
  'key-round': KeyRound,
  'landmark': Landmark,
  'layout-grid': LayoutGrid,
  'life-buoy': LifeBuoy,
  'lightbulb': Lightbulb,
  'link': Link,
  'loader-circle': LoaderCircle,
  'lock': Lock,
  'lock-open': LockOpen,
  'log-out': LogOut,
  'map-pin': MapPin,
  'menu': Menu,
  'message-circle': MessageCircle,
  'moon': Moon,
  'package': Package,
  'paperclip': Paperclip,
  'party-popper': PartyPopper,
  'pencil': Pencil,
  'pencil-ruler': PencilRuler,
  'play': Play,
  'plug': Plug,
  'plus': Plus,
  'receipt': Receipt,
  'refresh-cw': RefreshCw,
  'rocket': Rocket,
  'satellite-dish': SatelliteDish,
  'save': Save,
  'scale': Scale,
  'scroll-text': ScrollText,
  'search': Search,
  'send': Send,
  'settings': Settings,
  'shield': Shield,
  'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck,
  'signature': Signature,
  'siren': Siren,
  'sparkles': Sparkles,
  'square': Square,
  'star': Star,
  'stethoscope': Stethoscope,
  'store': Store,
  'sun': Sun,
  'target': Target,
  'trees': Trees,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'triangle-alert': TriangleAlert,
  'trophy': Trophy,
  'upload': Upload,
  'user': User,
  'user-cog': UserCog,
  'users': Users,
  'wallet': Wallet,
  'waves': Waves,
  'wrench': Wrench,
  'x': X,
  'zap': Zap,
}

const props = defineProps({
  /** Kebab-case Lucide icon name, must exist in the registry. */
  name: { type: String, required: true },
  /** House sizes only: 16 inline, 20 nav/buttons, 24 cards/empty states. */
  size: {
    type: Number,
    default: 20,
    validator: (v) => [16, 20, 24].includes(v),
  },
  /** Accessible label; when set the icon becomes role="img" instead of hidden. */
  label: { type: String, default: '' },
})

const icon = computed(() => {
  const found = registry[props.name]
  if (!found && import.meta.env?.DEV) {
    console.warn(`[AppIcon] Unknown icon name "${props.name}" — add it to the registry.`)
  }
  return found || CircleHelp
})
</script>

<template>
  <component
    :is="icon"
    class="app-icon"
    :size="size"
    :stroke-width="1.75"
    color="currentColor"
    :aria-hidden="label ? undefined : 'true'"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
  />
</template>

<style scoped>
.app-icon {
  /* Tailwind preflight sets `svg { display: block }`; icons must flow inline
     with text (headings, buttons, prose) unless a flex parent lays them out. */
  display: inline-block;
  flex-shrink: 0;
  vertical-align: -0.155em; /* optical baseline alignment when inline with text */
}
</style>
